import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { spring, prefersReducedMotion } from '../motion';

const OPEN = { response: 0.42, damping: 0.86 };
const SETTLE = { response: 0.36, damping: 0.8 };
const CLOSE = { response: 0.34, damping: 1 };
const DISMISS_PX = 110;
const DISMISS_SPEED = 700; // px per second
const TAP_PX = 6;

// Full-screen photo, iOS style. It zooms out of the thumbnail it was opened from (originRef)
// and back into it when closed. It can be dragged; letting go far or fast enough closes it,
// otherwise it springs back, keeping the speed of the finger.
// The animated state is [x, y, scale, backdrop opacity].
export default function ImageLightbox({ src, onClose, originRef }) {
  const [loaded, setLoaded] = useState(false);
  const backdrop = useRef();
  const wrap = useRef();
  const ctrl = useRef(null);
  const state = useRef([0, 0, 1, 0]);
  const drag = useRef(null);
  const closing = useRef(false);

  const render = useCallback(([x, y, s, bg]) => {
    state.current = [x, y, s, bg];
    if (wrap.current) wrap.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;
    if (backdrop.current) backdrop.current.style.opacity = String(Math.min(1, Math.max(0, bg)));
  }, []);

  const animate = useCallback((to, config, velocity = [0, 0, 0, 0], onComplete) => {
    ctrl.current?.stop();
    ctrl.current = spring({ from: state.current, to, velocity, config, precision: 0.01, onUpdate: render, onComplete });
  }, [render]);

  // The thumbnail's place as a state of the full image (null if it isn't on screen)
  const originState = useCallback(() => {
    const origin = originRef?.current;
    const img = wrap.current;
    if (!origin || !img || !origin.isConnected) return null;
    const o = origin.getBoundingClientRect();
    if (o.bottom < 0 || o.top > window.innerHeight || !o.width) return null;
    const saved = img.style.transform;
    img.style.transform = 'none';
    const r = img.getBoundingClientRect();
    img.style.transform = saved;
    if (!r.width) return null;
    return [o.left + o.width / 2 - (r.left + r.width / 2), o.top + o.height / 2 - (r.top + r.height / 2), o.width / r.width, 0];
  }, [originRef]);

  const close = useCallback((velocity = [0, 0, 0, 0]) => {
    if (closing.current) return;
    closing.current = true;
    if (prefersReducedMotion()) { onClose(); return; }
    // Back into the thumbnail, or away in the direction it was thrown
    const [x, y, s] = state.current;
    const dir = Math.sign(velocity[1] || y || 1);
    const away = [x, y + dir * window.innerHeight * 0.6, s * 0.9, 0];
    animate(originState() || away, CLOSE, velocity, onClose);
  }, [animate, onClose, originState]);

  // Open once the image has its size
  useEffect(() => {
    if (!loaded) return;
    render(originState() || [0, 24, 0.9, 0]);
    animate([0, 0, 1, 1], OPEN);
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [close]);
  useEffect(() => () => ctrl.current?.stop(), []);

  /* ── Dragging ── */

  const onPointerDown = (e) => {
    if (closing.current || e.button > 0) return;
    ctrl.current?.stop();
    const [x, y] = state.current;
    drag.current = {
      id: e.pointerId,
      sx: e.clientX - x / 0.6,
      sy: e.clientY - y,
      ox: e.clientX,
      oy: e.clientY,
      moved: false,
      samples: [[performance.now(), e.clientX, e.clientY]],
      onImage: !!wrap.current?.contains(e.target),
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.moved && Math.hypot(e.clientX - d.ox, e.clientY - d.oy) < TAP_PX) return;
    d.moved = true;
    d.samples.push([performance.now(), e.clientX, e.clientY]);
    if (d.samples.length > 6) d.samples.shift();
    const y = e.clientY - d.sy;
    const pull = Math.min(1, Math.abs(y) / 500);
    render([(e.clientX - d.sx) * 0.6, y, 1 - pull * 0.25, 1 - pull]);
  };
  const onPointerUp = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (!d.moved) {
      if (!d.onImage) close(); // tap next to the photo
      else animate([0, 0, 1, 1], SETTLE);
      return;
    }
    // Finger speed over the last moments
    const [t0, x0, y0] = d.samples[0];
    const [t1, x1, y1] = d.samples[d.samples.length - 1];
    const dt = Math.max((t1 - t0) / 1000, 0.016);
    const velocity = [((x1 - x0) / dt) * 0.6, (y1 - y0) / dt, 0, 0];
    if (Math.abs(state.current[1]) > DISMISS_PX || Math.abs(velocity[1]) > DISMISS_SPEED) close(velocity);
    else animate([0, 0, 1, 1], SETTLE, velocity);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ touchAction: 'none', cursor: 'zoom-out' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-noswipe
    >
      <div ref={backdrop} className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.92)', opacity: 0 }} />
      <button
        type="button"
        className="lb-close"
        style={{ zIndex: 1 }}
        onPointerDown={e => e.stopPropagation()}
        onClick={() => close()}
        aria-label="Close"
      >
        ✕
      </button>
      {!loaded && (
        <div style={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid rgba(255,255,255,0.15)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      )}
      <div ref={wrap} className="relative overflow-hidden rounded-[12px]" style={{ cursor: 'grab', willChange: 'transform', opacity: loaded ? 1 : 0 }}>
        <img src={src} alt="" draggable={false} className="block max-h-[95vh] max-w-[95vw] select-none object-contain" onLoad={() => setLoaded(true)} />
      </div>
    </div>,
    document.body
  );
}
